import React, { useState } from 'react';
import { GraduationCap, User, Calendar, FileText, ArrowUpRight } from 'lucide-react';
import { content, getText } from '../data/content';
import { theme, combineClasses, getCardStyle, getButtonStyle } from '../theme/theme';
import Modal from '../components/Modal';

/**
 * ============================================================================
 * 页面：课程教学 - Teaching.jsx
 * ============================================================================
 *
 * 【作用】课程教学页：页面标题/副标题、课程卡片网格；点击卡片打开详情弹窗（课程代码、级别、学期、描述、标签、可选信息、大纲链接）。
 * 【如何修改】课程数据在 src/data/content-teaching.js 的 courses 数组；页面标题等为 teaching.title / subtitle。
 *
 * 【扩展说明】
 * - 大纲：由“下载”改为链接。若课程有 syllabusUrl，显示“查看大纲”；站内路径用本页打开，http(s) 用新标签打开。
 * - 学期标签：若课程有 semestersOffered，在卡片/详情中显示“曾开课：…”标签。
 * - 可选区块：若课程有 optionalSections（如学分、课程目标），在详情弹窗中按顺序展示。
 *
 * 【参数说明】props: lang, themeMode。内部状态：selectedCourse（当前选中的课程对象与索引，用于弹窗）。
 */
const TeachingView = ({ lang, themeMode = "dark" }) => {
  const isLight = themeMode === "light";
  const [selectedCourse, setSelectedCourse] = useState(null);

  return (
    <>
    <div className="min-h-screen pt-8 pb-20 px-4 max-w-7xl mx-auto animate-fade-in">
      <div className="text-center mb-16">
        <h2 className={combineClasses(
          "text-4xl md:text-6xl font-bold mb-4",
          isLight ? "text-gray-900" : "text-white"
        )}>
          {getText('teaching.title', lang)}
        </h2>
        <p className={combineClasses(
          "text-lg max-w-2xl mx-auto",
          isLight ? "text-gray-600" : "text-gray-400"
        )}>{getText('teaching.subtitle', lang)}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {getText('teaching.courses', lang).map((course, index) => (
          <div
            key={index}
            className={combineClasses(
              "group relative backdrop-blur-md rounded-2xl overflow-hidden hover:border-cyan-500/50 transition-all duration-300 cursor-pointer border",
              getCardStyle(themeMode)
            )}
            onClick={() => setSelectedCourse({ ...course, index })}
          >
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyan-500 to-purple-500 opacity-50 group-hover:opacity-100 transition-opacity"></div>

            <div className="p-8">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <span className={combineClasses(
                    "inline-block px-3 py-1 rounded-md border text-cyan-400 font-mono text-sm font-bold mb-2",
                    isLight 
                      ? "bg-gray-100 border-gray-200" 
                      : "bg-white/5 border-white/10"
                  )}>
                    {course.code}
                  </span>
                  <h3 className={combineClasses(
                    "text-2xl font-bold transition-colors",
                    isLight 
                      ? "text-gray-900 group-hover:text-cyan-600" 
                      : "text-white group-hover:text-cyan-300"
                  )}>
                    {getText(`teaching.courses.${index}.title`, lang)}
                  </h3>
                </div>
                <div className={combineClasses(
                  "w-12 h-12 rounded-full flex items-center justify-center group-hover:bg-cyan-500/20 transition-colors",
                  isLight ? "bg-gray-100" : "bg-white/5"
                )}>
                  <GraduationCap className={combineClasses(
                    "w-6 h-6 group-hover:text-cyan-400",
                    isLight ? "text-gray-600" : "text-gray-400"
                  )} />
                </div>
              </div>

              <div className={combineClasses(
                "space-y-3 mb-6 text-sm",
                isLight ? "text-gray-700" : "text-gray-300"
              )}>
                <div className="flex items-center gap-3">
                  <User className={combineClasses(
                    "w-4 h-4",
                    isLight ? "text-gray-500" : "text-gray-500"
                  )} />
                  <span>
                    {getText('teaching.level', lang)}:{" "}
                    <span className={combineClasses(
                      "font-medium",
                      isLight ? "text-gray-900" : "text-white"
                    )}>
                      {getText(`teaching.courses.${index}.level`, lang)}
                    </span>
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className={combineClasses(
                    "w-4 h-4",
                    isLight ? "text-gray-500" : "text-gray-500"
                  )} />
                  <span>
                    {getText('teaching.term', lang)}:{" "}
                    <span className={combineClasses(
                      "font-medium",
                      isLight ? "text-gray-900" : "text-white"
                    )}>
                      {getText(`teaching.courses.${index}.term`, lang)}
                    </span>
                  </span>
                </div>
                {/* 可选：曾开课学期标签（同一门课开过多个学期时显示） */}
                {course.semestersOffered && course.semestersOffered.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={combineClasses(
                      "text-xs",
                      isLight ? "text-gray-500" : "text-gray-500"
                    )}>
                      {getText('teaching.previouslyOffered', lang)}:
                    </span>
                    {course.semestersOffered.map((sem, si) => (
                      <span
                        key={si}
                        className={combineClasses(
                          "text-xs px-2 py-0.5 rounded border",
                          isLight
                            ? "bg-cyan-50 text-cyan-700 border-cyan-200"
                            : "bg-cyan-500/10 text-cyan-300 border-cyan-500/20"
                        )}
                      >
                        {typeof sem === 'object' && sem !== null && (sem.en !== undefined || sem.cn !== undefined)
                          ? (sem[lang] ?? sem.en ?? sem.cn)
                          : sem}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <p className={combineClasses(
                "leading-relaxed mb-8 border-t pt-4",
                isLight 
                  ? "text-gray-700 border-gray-200" 
                  : "text-gray-400 border-white/5"
              )}>
                {getText(`teaching.courses.${index}.abstract`, lang) ?? getText(`teaching.courses.${index}.desc`, lang)}
              </p>

              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  {getText(`teaching.courses.${index}.tags`, lang).map((tag, i) => (
                    <span
                      key={i}
                      className={combineClasses(
                        "text-xs px-2 py-1 rounded border",
                        isLight 
                          ? "bg-gray-100 text-gray-600 border-gray-200" 
                          : "bg-white/5 text-gray-500 border-white/5"
                      )}
                    >
                      #{getText(`teaching.courses.${index}.tags.${i}`, lang)}
                    </span>
                  ))}
                </div>
                {/* 大纲：有 syllabusUrl 时显示为链接（站内路径本页打开，http(s) 新标签打开）；无则不再显示下载按钮 */}
                {course.syllabusUrl && (
                  <a
                    href={course.syllabusUrl}
                    target={course.syllabusUrl.startsWith('http') ? '_blank' : undefined}
                    rel={course.syllabusUrl.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className={combineClasses(
                      "flex items-center gap-2 text-sm font-bold transition-colors group/btn",
                      isLight 
                        ? "text-cyan-600 hover:text-gray-900" 
                        : "text-cyan-400 hover:text-white"
                    )}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <FileText className="w-4 h-4" />
                    {getText('teaching.viewSyllabus', lang)}
                    <ArrowUpRight className="w-3 h-3 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform" />
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* 课程详情模态框 */}
    {selectedCourse && (
      <Modal
        isOpen={!!selectedCourse}
        onClose={() => setSelectedCourse(null)}
        title={getText(`teaching.courses.${selectedCourse.index}.title`, lang)}
        size="lg"
        themeMode={themeMode}
      >
        <div className="space-y-6">
          {/* 标题已在 Modal 头部展示，此处仅保留课程代码与图标 */}
          <div className={combineClasses(
            "flex items-center justify-between gap-4 pb-4 border-b",
            isLight ? "border-gray-200" : "border-white/10"
          )}>
            <span className={combineClasses(
              "inline-block px-3 py-1 rounded-md border text-cyan-400 font-mono text-sm font-bold",
              isLight 
                ? "bg-gray-100 border-gray-200" 
                : "bg-white/5 border-white/10"
            )}>
              {selectedCourse.code}
            </span>
            <div className={combineClasses(
              "w-12 h-12 rounded-full flex items-center justify-center",
              isLight ? "bg-gray-100" : "bg-white/5"
            )}>
              <GraduationCap className="w-6 h-6 text-cyan-400" />
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <User className="w-4 h-4 text-gray-500" />
              <span className={isLight ? "text-gray-700" : "text-gray-300"}>
                {getText('teaching.level', lang)}:{" "}
                <span className={combineClasses(
                  "font-medium",
                  isLight ? "text-gray-900" : "text-white"
                )}>
                  {getText(`teaching.courses.${selectedCourse.index}.level`, lang)}
                </span>
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Calendar className="w-4 h-4 text-gray-500" />
              <span className={isLight ? "text-gray-700" : "text-gray-300"}>
                {getText('teaching.term', lang)}:{" "}
                <span className={combineClasses(
                  "font-medium",
                  isLight ? "text-gray-900" : "text-white"
                )}>
                  {getText(`teaching.courses.${selectedCourse.index}.term`, lang)}
                </span>
              </span>
            </div>
            {/* 可选：曾开课学期（详情弹窗内） */}
            {selectedCourse.semestersOffered && selectedCourse.semestersOffered.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className={isLight ? "text-gray-600" : "text-gray-400"}>
                  {getText('teaching.previouslyOffered', lang)}:
                </span>
                {selectedCourse.semestersOffered.map((sem, si) => (
                  <span
                    key={si}
                    className={combineClasses(
                      "text-xs px-2 py-0.5 rounded border",
                      isLight
                        ? "bg-cyan-50 text-cyan-700 border-cyan-200"
                        : "bg-cyan-500/10 text-cyan-300 border-cyan-500/20"
                    )}
                  >
                    {typeof sem === 'object' && sem !== null && (sem.en !== undefined || sem.cn !== undefined)
                      ? (sem[lang] ?? sem.en ?? sem.cn)
                      : sem}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className={combineClasses(
            "pt-4 border-t",
            isLight ? "border-gray-200" : "border-white/10"
          )}>
            <h4 className={combineClasses(
              "font-semibold mb-3",
              isLight ? "text-gray-900" : "text-white"
            )}>
              {lang === 'cn' ? '课程描述' : 'Course Description'}
            </h4>
            <p className={combineClasses(
              "leading-relaxed",
              isLight ? "text-gray-700" : "text-gray-300"
            )}>
              {getText(`teaching.courses.${selectedCourse.index}.desc`, lang)}
            </p>
          </div>

          {/* 可选区块：学分、课程目标等，在 content-teaching.js 的 optionalSections 中配置 */}
          {content?.teaching?.courses?.[selectedCourse.index]?.optionalSections?.length > 0 && (
            <div className={combineClasses(
              "pt-4 border-t space-y-4",
              isLight ? "border-gray-200" : "border-white/10"
            )}>
              {content.teaching.courses[selectedCourse.index].optionalSections.map((_, i) => (
                <div key={i}>
                  <h4 className={combineClasses(
                    "font-semibold mb-2",
                    isLight ? "text-gray-900" : "text-white"
                  )}>
                    {getText(`teaching.courses.${selectedCourse.index}.optionalSections.${i}.label`, lang)}
                  </h4>
                  <p className={combineClasses(
                    "leading-relaxed text-sm",
                    isLight ? "text-gray-700" : "text-gray-300"
                  )}>
                    {getText(`teaching.courses.${selectedCourse.index}.optionalSections.${i}.content`, lang)}
                  </p>
                </div>
              ))}
            </div>
          )}

          <div className={combineClasses(
            "pt-4 border-t",
            isLight ? "border-gray-200" : "border-white/10"
          )}>
            <div className="flex gap-2 flex-wrap mb-4">
              {getText(`teaching.courses.${selectedCourse.index}.tags`, lang).map((tag, i) => (
                <span
                  key={i}
                  className={combineClasses(
                    "text-xs px-2 py-1 rounded border",
                    isLight 
                      ? "bg-gray-100 text-gray-600 border-gray-200" 
                      : "bg-white/5 text-gray-500 border-white/5"
                  )}
                >
                  #{getText(`teaching.courses.${selectedCourse.index}.tags.${i}`, lang)}
                </span>
              ))}
            </div>
            {/* 大纲链接：有 syllabusUrl 时显示，外部链接新开标签 */}
            {selectedCourse.syllabusUrl && (
              <a
                href={selectedCourse.syllabusUrl}
                target={selectedCourse.syllabusUrl.startsWith('http') ? '_blank' : undefined}
                rel={selectedCourse.syllabusUrl.startsWith('http') ? 'noopener noreferrer' : undefined}
                className={combineClasses(
                  getButtonStyle(themeMode, 'secondary'),
                  "inline-flex items-center gap-2"
                )}
              >
                <FileText className="w-4 h-4" />
                {getText('teaching.viewSyllabus', lang)}
                <ArrowUpRight className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>
      </Modal>
    )}
    </>
  );
};

export default TeachingView;
